import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DestinationPreset,
  getPresetMaxDurationKey,
} from 'blocks/DestinationPlatforms';
import { AudioClipperComponent } from 'components/AudioClipper';
import { Step } from 'components/Wizard';
import { DEFAULT_LANGUAGE } from 'constants/languages';
import { CustomizeStepSubmitType } from 'containers/ProjectWizards/CustomizeStep';
import { TranscriptionFormValue } from 'containers/ProjectWizards/types';
import { onCompleteWizard, onWizardNext } from 'redux/modules/mixpanel';
import { getMixpanelBlurValue } from 'redux/modules/mixpanel/utils';
import { goToCreate, goToProject } from 'redux/modules/router';
import { clearSelectedAudio } from 'redux/modules/sample-audio';
import {
  clearEntireAudio,
  createTemplateProject,
} from 'redux/modules/template-wizard/actions';
import { templateSelector } from 'redux/modules/template-wizard/selectors';
import { projectIdSelector } from 'redux/modules/wizard-export/selectors';
import { Dispatch } from 'redux/types';
import { ImageOriginId, SoundwavePositionValue } from 'types';
import { createClippingOption } from 'utils/audio';
import {
  getBlurRadius,
  hasSlideFromOrigin,
  isAudiogram,
} from 'utils/embed/embed';
import {
  getPositionIdFromWave,
  mapSoundwaveGeneration,
} from 'utils/embed/soundwave';
import { TemplateWizardFlowHook } from '../../types';
import { block } from '../../utils';
import { MIXPANEL_WIZARD_STEP_MAP } from './constants';
import { AudioFlowStep } from './types';
import useAddAudioStep from './useAddAudioStep';
import useAudioTemplateFlowBlocker from './useAudioTemplateFlowBlocker';
import useClipAudioStep from './useClipAudioStep';
import useCustomizeStep from './useCustomizeStep';
import useExportingStep from './useExportingStep';
import useProgressStep from './useProgressStep';

const useAudioTemplateFlow: TemplateWizardFlowHook<AudioFlowStep> = ({
  onError,
  wizardRef,
}) => {
  const template = useSelector(templateSelector);

  const [step, setStep] = useState<AudioFlowStep>();
  const [selectedPreset, setSelectedPreset] = useState<DestinationPreset>();
  const [clipDurationMillis, setClipDurationMillis] = useState<number>(
    undefined,
  );
  const [transcription, setTranscription] = useState<TranscriptionFormValue>({
    language: DEFAULT_LANGUAGE,
    transcribe: false,
  });

  const clipperRef = useRef<AudioClipperComponent>();
  const submitTypeRef = useRef<CustomizeStepSubmitType>();

  const dispatch = useDispatch<Dispatch>();

  const dimensions = template?.dimension;

  const {
    afterUploadTransfer,
    audioRegion: defaultAudioRegion,
    audioSourceType,
    podcastIdentifier,
    resetAudioState,
    source,
    step: audioStep,
  } = useAddAudioStep({
    onAudioAdded: src => {
      if (src) {
        wizardRef.current.next();
      }
    },
    onError: error => onError(error, true),
  });

  const { step: clipAudioStep } = useClipAudioStep({
    clipperRef: clipperRef as any,
    hidden: step !== AudioFlowStep.CLIP_AUDIO,
    onClearError: defaultAudioRegion.setSelected,
    onError: defaultAudioRegion.setUnselected,
    onReady: defaultAudioRegion.setSelected,
    onRegionUpdate: region => {
      setClipDurationMillis(region.endMillis - region.startMillis);
    },
    onPresetChange: setSelectedPreset,
    onTranscriptionChange: setTranscription,
    regionSelected: defaultAudioRegion !== undefined,
    src: source,
    transcription,
  });

  const { step: customizeStep } = useCustomizeStep({
    podcastIdentifier,
    aspectRatio: dimensions?.width / dimensions?.height,
    durationMillis: clipDurationMillis,
    transcription,
    source,
    onSubmit: useCallback(
      ({ type, projectName, transcriptionEnabled }, config) => {
        wizardRef.current.next();
        submitTypeRef.current = type;
        const audioClip = clipperRef.current.clip;

        afterUploadTransfer(async () => {
          try {
            dispatch(
              // TODO helper to create this action from config
              onCompleteWizard({
                templateId: template?.templateId,
                addedImage: config.slideshow?.length > 0,
                addedText: config.textOverlays?.length > 0,
                audioSource: audioSourceType,
                backgroundColor: config.backgroundColor,
                canvaImage: hasSlideFromOrigin(ImageOriginId.CANVA, config),
                enabledProgressBar: config.progress.enabled,
                length: isAudiogram(audioClip) ? 'short' : 'long',
                originalAudioDuration: audioClip.originalDurationMillis,
                submissionType: type,
                transcription: transcriptionEnabled,
                type: 'templateWizard',
                waveColor: config.soundwave?.waveColor,
                waveGeneration: mapSoundwaveGeneration(
                  config.soundwave?.waveGeneration,
                ),
                wavePosition: getPositionIdFromWave(
                  config.soundwave,
                ) as SoundwavePositionValue,
                waveStyle: config.soundwave?.waveType,
                blurValue: getMixpanelBlurValue(getBlurRadius(config)),
                ...createClippingOption(audioClip),
              }),
            );

            const submitConfig = {
              ...config,
              audioClip,
              audioSourceType,
              projectName,
              templateId: template?.templateId,
              aspectRatio: dimensions,
              transcription: {
                ...transcription,
                transcribe: transcriptionEnabled,
              },
            };

            await dispatch(createTemplateProject(type, submitConfig));
          } catch (err) {
            onError(err, true);
          }
        })();
      },
      [
        afterUploadTransfer,
        audioSourceType,
        dimensions,
        dispatch,
        onError,
        template?.templateId,
        transcription,
        wizardRef,
      ],
    ),
    onTemplatesClick: () => wizardRef.current.previous(),
    onTranscriptionChange: setTranscription,
  });

  const { step: progressStep } = useProgressStep({
    onCompleted: () => {
      if (isAudiogram(clipDurationMillis) && submitTypeRef.current === 'edit') {
        dispatch((_, getState) => {
          const projectId = projectIdSelector(getState());
          dispatch(
            goToProject({ projectId, history: false, fromWizard: true }),
          );
        });
      } else {
        wizardRef.current.next();
      }
    },
    onError,
  });

  const { step: exportingStep } = useExportingStep({
    durationMillis: clipDurationMillis,
    onError: error => {
      wizardRef.current?.jump('customize');
      onError(error);
    },
  });

  const confirm = useAudioTemplateFlowBlocker(step);

  const handleCancel = useCallback(() => {
    dispatch(goToCreate());
  }, [dispatch]);

  const handleStepChange = useCallback(
    (dest: Step<AudioFlowStep>, src: Step<AudioFlowStep>) => {
      setStep(dest.stepId);

      if (src) {
        dispatch(
          onWizardNext({
            templateId: template?.templateId,
            step: MIXPANEL_WIZARD_STEP_MAP[dest.stepId],
            type: 'templateWizard',
            ...(src.stepId === 'clip-audio' && {
              destination: selectedPreset?.key ?? 'Custom',
              maxDuration: getPresetMaxDurationKey(selectedPreset?.key),
            }),
          }),
        );
      }

      // When accessing the step, selected preset should be reset when re entering
      // the clip step from the add-audio one.
      if (
        dest.stepId === AudioFlowStep.CLIP_AUDIO &&
        src.stepId === AudioFlowStep.ADD_AUDIO
      ) {
        setSelectedPreset(undefined);
      }

      if (dest.stepId === AudioFlowStep.ADD_AUDIO) {
        resetAudioState();
        dispatch(clearEntireAudio());
        dispatch(clearSelectedAudio());
      }
    },
    [dispatch, resetAudioState, selectedPreset?.key, template?.templateId],
  );

  return {
    bodyClassName: block('body', { [step]: true }),
    canBacktrack:
      step !== AudioFlowStep.SUBMITTING && step !== AudioFlowStep.EXPORTING,
    onCancelClick: handleCancel,
    onStepChange: handleStepChange,
    onStepClick: confirm,
    steps: [
      audioStep,
      clipAudioStep,
      customizeStep,
      progressStep,
      exportingStep,
    ],
  };
};

export default useAudioTemplateFlow;
